/**
 * usePlacementOfferten — TanStack Query hooks for the Ausschreibungs-Screen Tab 4a (Offert-Posteingang
 * + IDP, Prozessschritte 9/10; P2 Placement Slice 10).
 *
 * Server state = TanStack Query ONLY. live mode hits apiClient.placements.*Offert; mock mode resolves
 * the Ü5 fixture (AXA SUB-2026-000482-HAFT with two normalised versions · Zurich …483 manuell zu
 * pruefen · Helvetia …484 no offer yet · one nicht-zugeordnet upload). The server owns every decision;
 * the UI only triggers + renders. Every mutation invalidates the offerten key AND the submissions key
 * (the Submission chip flips to «eingegangen»).
 *
 * [Source: apps/web/src/features/placements/usePlacementQna.ts] (live/mock + qk + invalidate idiom)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import {
  OFFER_IDP_STATUS_LABELS,
  OFFER_STATUS_LABELS,
  offerZaehler,
} from '@brokly/shared';
import type {
  OfferEintrag,
  OfferListResponse,
  OfferNormalisiertBody,
  OfferNormalizedV1,
  OfferSubmissionGruppe,
  OfferVerwerfenBody,
  OfferZuordnenBody,
} from '@brokly/shared';

// ── Ü5 mock fixture (mock mode only) ─────────────────────────────────────────────

function eintrag(over: Partial<OfferEintrag> & { id: string }): OfferEintrag {
  return {
    document_id: `doc-${over.id}`,
    communication_id: `comm-${over.id}`,
    source: 'posteingang',
    submission_id: null,
    submission_number: null,
    carrier_uid: null,
    carrier_name: null,
    logo_url: null,
    status: 'eingegangen',
    status_label: OFFER_STATUS_LABELS.eingegangen,
    idp_status: 'ausstehend',
    idp_status_label: OFFER_IDP_STATUS_LABELS.ausstehend,
    match_art: 'thread',
    version_no: null,
    is_current: false,
    abgeloest_at: null,
    filename: 'offerte.pdf',
    betreff: null,
    absender: null,
    received_at: '2026-10-09T08:00:00.000Z',
    thumbnail_ref: null,
    datei_url: `/placements/pl/offerten/${over.id}/datei`,
    extractor_driver: null,
    extractor_confidence: null,
    extraction_seq: 1,
    normalized: null,
    normalisiert_at: null,
    normalisiert_by_name: null,
    manuell_ergaenzt: false,
    pruef_task_id: null,
    fehler_task_id: null,
    zugeordnet_at: null,
    verworfen_at: null,
    verworfen_grund: null,
    bearbeitbar: true,
    created_at: '2026-10-09T08:00:00.000Z',
    updated_at: '2026-10-09T08:00:00.000Z',
    ...over,
  };
}

function normalizedAxa(): OfferNormalizedV1 {
  return {
    schema_version: 1,
    waehrung: 'CHF',
    praemie: { jahr: 18400, netto: null, brutto: null },
    versicherungssumme: 5000000,
    selbstbehalt: 2500,
    vertragsdauer_monate: 12,
    beginn: null,
    gueltig_bis: '2026-12-04',
    offert_nummer: 'OFF-AXA-2026-0482',
    versicherer_name: 'AXA',
    deckungen: [
      { rule_key: 'betriebshaftpflicht_deckung', label: 'Betriebshaftpflicht', pflicht_status: 'empfehlung', status: 'eingeschlossen', deckungssumme: 5000000, selbstbehalt: 2500, hinweis: null, soll_zielsumme: 5000000, soll_selbstbehalt: null, match: 'rule_key' },
      { rule_key: null, label: 'Produkthaftpflicht', pflicht_status: null, status: 'eingeschlossen', deckungssumme: null, selbstbehalt: null, hinweis: null, soll_zielsumme: null, soll_selbstbehalt: null, match: 'keine' },
      { rule_key: null, label: 'Rueckrufkosten', pflicht_status: null, status: 'eingeschlossen', deckungssumme: null, selbstbehalt: null, hinweis: null, soll_zielsumme: null, soll_selbstbehalt: null, match: 'keine' },
      { rule_key: null, label: 'USA/Kanada', pflicht_status: null, status: 'eingeschlossen', deckungssumme: null, selbstbehalt: null, hinweis: null, soll_zielsumme: null, soll_selbstbehalt: null, match: 'keine' },
    ],
    ausschluesse: ['Asbest', 'Cyber-Schaeden'],
    soll_abdeckung: { total: 1, gematcht: 1, unbekannt: 0 },
    kernwerte_vollstaendig: true,
    quelle: { extractor: 'fixture', confidence: 0.93, extracted_at: '2026-10-09T08:00:00.000Z', manuell_ergaenzt: false },
  };
}

function normalizedZurich(): OfferNormalizedV1 {
  return {
    schema_version: 1,
    waehrung: 'CHF',
    praemie: { jahr: null, netto: null, brutto: null },
    versicherungssumme: 5000000,
    selbstbehalt: 5000,
    vertragsdauer_monate: 12,
    beginn: null,
    gueltig_bis: '2026-12-04',
    offert_nummer: 'OFF-ZUR-2026-0483',
    versicherer_name: 'Zurich',
    deckungen: [
      { rule_key: 'betriebshaftpflicht_deckung', label: 'Betriebshaftpflicht', pflicht_status: 'empfehlung', status: 'eingeschlossen', deckungssumme: 5000000, selbstbehalt: 5000, hinweis: null, soll_zielsumme: 5000000, soll_selbstbehalt: null, match: 'rule_key' },
      { rule_key: null, label: 'Rueckrufkosten', pflicht_status: null, status: 'ausgeschlossen', deckungssumme: null, selbstbehalt: null, hinweis: null, soll_zielsumme: null, soll_selbstbehalt: null, match: 'keine' },
    ],
    ausschluesse: ['Asbest'],
    soll_abdeckung: { total: 1, gematcht: 1, unbekannt: 0 },
    kernwerte_vollstaendig: false,
    quelle: { extractor: 'fixture', confidence: 0.55, extracted_at: '2026-10-07T08:00:00.000Z', manuell_ergaenzt: false },
  };
}

function mockOfferten(id: string): OfferListResponse {
  const axaV1 = eintrag({
    id: 'off-axa-1', submission_id: 'sub-axa', submission_number: 'SUB-2026-000482-HAFT', carrier_uid: 'CHE-AXA', carrier_name: 'AXA',
    status: 'normalisiert', status_label: OFFER_STATUS_LABELS.normalisiert, idp_status: 'normalisiert', idp_status_label: OFFER_IDP_STATUS_LABELS.normalisiert,
    version_no: 1, is_current: false, abgeloest_at: '2026-10-09T08:00:00.000Z', filename: 'Offerte_AXA_v1.pdf', received_at: '2026-10-06T08:00:00.000Z',
    extractor_driver: 'fixture', extractor_confidence: 0.93, normalized: normalizedAxa(), normalisiert_at: '2026-10-06T08:05:00.000Z', zugeordnet_at: '2026-10-06T08:00:00.000Z',
  });
  const axaV2 = eintrag({
    id: 'off-axa-2', submission_id: 'sub-axa', submission_number: 'SUB-2026-000482-HAFT', carrier_uid: 'CHE-AXA', carrier_name: 'AXA',
    status: 'normalisiert', status_label: OFFER_STATUS_LABELS.normalisiert, idp_status: 'normalisiert', idp_status_label: OFFER_IDP_STATUS_LABELS.normalisiert,
    version_no: 2, is_current: true, filename: 'Offerte_AXA_v2.pdf', received_at: '2026-10-09T08:00:00.000Z',
    extractor_driver: 'fixture', extractor_confidence: 0.93, normalized: normalizedAxa(), normalisiert_at: '2026-10-09T08:05:00.000Z', zugeordnet_at: '2026-10-09T08:00:00.000Z',
  });
  const zur = eintrag({
    id: 'off-zur-1', submission_id: 'sub-zur', submission_number: 'SUB-2026-000483-HAFT', carrier_uid: 'CHE-ZUR', carrier_name: 'Zurich',
    status: 'eingegangen', idp_status: 'manuell_zu_pruefen', idp_status_label: OFFER_IDP_STATUS_LABELS.manuell_zu_pruefen,
    version_no: 1, is_current: true, filename: 'Offerte_Zurich.pdf', received_at: '2026-10-07T08:00:00.000Z',
    extractor_driver: 'fixture', extractor_confidence: 0.55, normalized: normalizedZurich(), pruef_task_id: 'task-zur', zugeordnet_at: '2026-10-07T08:00:00.000Z',
  });
  const mobi = eintrag({
    id: 'off-mobi-1', source: 'upload', match_art: null, carrier_name: null,
    status: 'eingegangen', idp_status: 'ausstehend', filename: 'Offerte_Mobiliar.pdf', received_at: '2026-10-09T10:00:00.000Z', absender: 'Max Broker',
  });

  const gruppen: OfferSubmissionGruppe[] = [
    { submission_id: 'sub-axa', submission_number: 'SUB-2026-000482-HAFT', carrier_uid: 'CHE-AXA', name: 'AXA', logo_url: null, submission_status: 'eingegangen', zuordenbar: true, aktuelle: axaV2, items: [axaV2, axaV1] },
    { submission_id: 'sub-zur', submission_number: 'SUB-2026-000483-HAFT', carrier_uid: 'CHE-ZUR', name: 'Zurich', logo_url: null, submission_status: 'eingegangen', zuordenbar: true, aktuelle: zur, items: [zur] },
    { submission_id: 'sub-hel', submission_number: 'SUB-2026-000484-HAFT', carrier_uid: 'CHE-HEL', name: 'Helvetia', logo_url: null, submission_status: 'versandt', zuordenbar: true, aktuelle: null, items: [] },
    { submission_id: null, submission_number: null, carrier_uid: null, name: null, logo_url: null, submission_status: null, zuordenbar: false, aktuelle: null, items: [mobi] },
  ];
  const flat = gruppen.flatMap((g) => g.items);
  return {
    placement_id: id,
    placement_number: 'PL-2026-000001',
    status: 'ausgeschrieben',
    branchencode: '21000',
    aktion_erlaubt: true,
    sperrgrund: null,
    zaehler: offerZaehler(flat),
    extractor: { driver: 'fixture', is_demo: true },
    gruppen,
  };
}

// ── Query ──────────────────────────────────────────────────────────────────────

export function usePlacementOfferten(id: string) {
  const live = isLiveData();
  return useQuery<OfferListResponse>({
    queryKey: qk.placementOfferten(id, live ? 'live' : 'mock'),
    queryFn: () => (live ? apiClient.placements.getOfferten(id) : Promise.resolve(mockOfferten(id))),
    ...(live ? {} : { initialData: () => mockOfferten(id), staleTime: Infinity }),
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

function useOffertInvalidate(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['placements', 'offerten', id] });
    void qc.invalidateQueries({ queryKey: ['placements', 'submissions', id] });
  };
}

export function useUploadOffert(id: string) {
  const live = isLiveData();
  const invalidate = useOffertInvalidate(id);
  return useMutation({
    mutationFn: ({ file, submissionId }: { file: File; submissionId?: string }) =>
      live ? apiClient.placements.uploadOffert(id, file, submissionId) : Promise.resolve(mockOfferten(id)),
    onSuccess: invalidate,
  });
}

export function useZuordnenOffert(id: string) {
  const live = isLiveData();
  const invalidate = useOffertInvalidate(id);
  return useMutation({
    mutationFn: ({ oid, body }: { oid: string; body: OfferZuordnenBody }) =>
      live ? apiClient.placements.zuordnenOffert(id, oid, body) : Promise.resolve(mockOfferten(id)),
    onSuccess: invalidate,
  });
}

export function useAktuellOffert(id: string) {
  const live = isLiveData();
  const invalidate = useOffertInvalidate(id);
  return useMutation({
    mutationFn: ({ oid }: { oid: string }) =>
      live ? apiClient.placements.aktuellOffert(id, oid) : Promise.resolve(mockOfferten(id)),
    onSuccess: invalidate,
  });
}

export function useReExtractOffert(id: string) {
  const live = isLiveData();
  const invalidate = useOffertInvalidate(id);
  return useMutation({
    mutationFn: ({ oid }: { oid: string }) =>
      live ? apiClient.placements.reExtractOffert(id, oid) : Promise.resolve(mockOfferten(id)),
    onSuccess: invalidate,
  });
}

export function useNormalisiertOffert(id: string) {
  const live = isLiveData();
  const invalidate = useOffertInvalidate(id);
  return useMutation({
    mutationFn: ({ oid, body }: { oid: string; body: OfferNormalisiertBody }) =>
      live ? apiClient.placements.normalisiertOffert(id, oid, body) : Promise.resolve(mockOfferten(id)),
    onSuccess: invalidate,
  });
}

export function useVerwerfenOffert(id: string) {
  const live = isLiveData();
  const invalidate = useOffertInvalidate(id);
  return useMutation({
    mutationFn: ({ oid, body }: { oid: string; body: OfferVerwerfenBody }) =>
      live ? apiClient.placements.verwerfenOffert(id, oid, body) : Promise.resolve(mockOfferten(id)),
    onSuccess: invalidate,
  });
}
